/**
 * The "who you are" questionnaire, drawn into #profileRoot in Settings.
 * Short, plain questions about the player rather than their shooting, all of
 * them skippable. The answers go straight into the companion's prompt, so this
 * card says that out loud and shows how much is filled in.
 */

let ctx = null;
let root = null;
let questions = [];
let saved = {};   // what the server holds
let draft = {};   // what is on screen right now

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const $ = (id) => document.getElementById(id);
const answered = (v) => (Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '');
const norm = (v) => (Array.isArray(v) ? [...v].sort().join('|') : String(v ?? '').trim());

export function mount(c) {
  ctx = c;
  root = $('profileRoot');
  if (!root) return;
  root.innerHTML = `
    <div class="card prof">
      <h3>What your companion should know about you</h3>
      <p class="prof-why">Your companion reads these answers before every reply, so the coaching fits you and not just your last twelve shots.</p>
      <div class="prof-head">
        <div class="bar"><i id="profBar"></i></div>
        <small id="profCount">Loading your answers.</small>
      </div>
      <div class="prof-qs" id="profQs"></div>
      <div class="prof-foot">
        <button class="btn primary" id="profSave">Save what you changed</button>
        <small>Every question is optional. Tap an answer again to clear it.</small>
      </div>
    </div>`;

  // One set of listeners for the life of the page: the questions repaint under them.
  root.addEventListener('click', (e) => {
    const pill = e.target.closest('.prof-pill');
    if (pill) { pick(pill.dataset.id, pill.dataset.opt); return; }
    if (e.target.closest('#profSave')) save();
  });
  root.addEventListener('input', (e) => {
    const box = e.target.closest('.prof-text');
    if (!box) return;
    draft[box.dataset.id] = box.value;
    grow(box); paintCount();
  });
}

export async function render() {
  if (!root) return;
  try {
    const data = await ctx.api('/api/profile');
    questions = Array.isArray(data.questions) ? data.questions : [];
    saved = { ...(data.profile?.answers || {}) };
    draft = { ...saved };
    paint();
  } catch (e) {
    questions = [];
    $('profQs').innerHTML = `<p class="prof-err">Could not load your answers: ${esc(e.message)}</p>`;
    $('profCount').textContent = 'Not loaded yet.';
    $('profBar').style.width = '0%';
  }
}

/* ------------------------------------------------------------------ paint */

function paint() {
  $('profQs').innerHTML = questions.map(question).join('');
  $('profQs').querySelectorAll('.prof-text').forEach(grow);
  paintCount();
}

function question(q) {
  const value = draft[q.id];
  const head = `<b>${esc(q.label)}</b><small>${esc(q.help)}</small>`;
  if (q.type === 'text') {
    return `<div class="prof-q">${head}
      <textarea class="prof-text" data-id="${esc(q.id)}" rows="2" maxlength="${Number(q.maxLength) || 300}"
        placeholder="${esc(q.placeholder)}">${esc(value || '')}</textarea></div>`;
  }
  const picked = q.type === 'multi' ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  const pills = (q.options || []).map((o) =>
    `<button type="button" class="prof-pill${picked.includes(o) ? ' on' : ''}" data-id="${esc(q.id)}" data-opt="${esc(o)}">${esc(o)}</button>`).join('');
  return `<div class="prof-q">${head}<div class="prof-opts">${pills}</div></div>`;
}

function paintCount() {
  const done = questions.filter((q) => answered(draft[q.id])).length;
  $('profCount').textContent = questions.length ? `${done} of ${questions.length} answered` : 'No questions to answer.';
  $('profBar').style.width = `${questions.length ? Math.round((done / questions.length) * 100) : 0}%`;
}

/** Textareas grow with what is typed in them, up to a point. */
function grow(box) {
  box.style.height = 'auto';
  if (box.scrollHeight) box.style.height = `${Math.min(box.scrollHeight + 2, 260)}px`;
}

/* --------------------------------------------------------------- answering */

function pick(id, option) {
  const q = questions.find((x) => x.id === id);
  if (!q) return;
  if (q.type === 'multi') {
    const list = Array.isArray(draft[id]) ? [...draft[id]] : [];
    const at = list.indexOf(option);
    if (at === -1) list.push(option); else list.splice(at, 1);
    draft[id] = list;
  } else {
    draft[id] = draft[id] === option ? '' : option;   // tapping the same one clears it
  }
  const chosen = q.type === 'multi' ? draft[id] : [draft[id]];
  root.querySelectorAll(`.prof-pill[data-id="${CSS.escape(id)}"]`).forEach((b) =>
    b.classList.toggle('on', chosen.includes(b.dataset.opt)));
  paintCount();
}

async function save() {
  const changed = {};
  for (const q of questions) {
    if (norm(draft[q.id]) === norm(saved[q.id])) continue;
    changed[q.id] = draft[q.id] ?? (q.type === 'multi' ? [] : '');
  }
  if (!Object.keys(changed).length) { ctx.toast('Nothing new to save yet.'); return; }
  const button = $('profSave');
  button.disabled = true;
  try {
    const res = await ctx.api('/api/profile', { method: 'PATCH', body: JSON.stringify({ answers: changed }) });
    saved = { ...(res.profile?.answers || {}) };
    draft = { ...saved };
    paint();
    ctx.toast('Saved. Your companion has it.');
  } catch (e) {
    ctx.toast(e.message, true);
  } finally {
    button.disabled = false;
  }
}
